import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Divider,
  Button,
  Stack,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  CheckCircleOutline,
  Phone,
  Email,
  Language,
  LocationOn,
  Sms,
  ErrorOutline,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ClinicService from '../../../services/clinic.service';
import SmsConfirmationHours from './SmsConfirmationHours';

const PRIMARY = '#1565C0';
const SUCCESS = '#2E7D32';

function formatWebsiteUrl(website) {
  if (!website) return '';
  if (website.startsWith('http://') || website.startsWith('https://')) {
    return website;
  }
  return `https://${website}`;
}

function formatAddress(clinic) {
  return [clinic.street, clinic.city, clinic.postalCode, clinic.country]
    .filter(Boolean)
    .join(', ');
}

function ContactItem({ icon, label, value, href }) {
  const content = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': href
          ? {
              borderColor: PRIMARY,
              backgroundColor: alpha(PRIMARY, 0.04),
              transform: 'translateY(-2px)',
            }
          : {},
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 1.5,
            backgroundColor: alpha(PRIMARY, 0.08),
            color: PRIMARY,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: href ? PRIMARY : 'text.primary',
              wordBreak: 'break-word',
            }}
          >
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  if (href) {
    return (
      <Box
        component="a"
        href={href}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
        sx={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
      >
        {content}
      </Box>
    );
  }

  return content;
}

function SmsConfirmation() {
  const { clinicId, messageId } = useParams();
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    ClinicService.getGatewayClinicInfo(clinicId, messageId)
      .then((res) => {
        setClinic(res.data);
        setLoading(false);
        setError(null);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        setError('We could not load your confirmation. Please try again later.');
      });
  }, [clinicId, messageId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(160deg, #f5f8fc 0%, #e8eef6 100%)',
        }}
      >
        <CircularProgress size={48} thickness={4} sx={{ color: PRIMARY }} />
        <Typography variant="body1" sx={{ mt: 3, color: 'text.secondary', fontWeight: 500 }}>
          Loading your confirmation...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(160deg, #f5f8fc 0%, #e8eef6 100%)',
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 440,
            width: '100%',
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: alpha('#d32f2f', 0.2),
            backgroundColor: '#fff',
          }}
        >
          <ErrorOutline sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const address = formatAddress(clinic);
  const websiteUrl = formatWebsiteUrl(clinic.website);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #f5f8fc 0%, #e8eef6 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component="header"
        sx={{
          py: { xs: 2, md: 3 },
          px: 2,
          backgroundColor: '#fff',
          borderBottom: '1px solid',
          borderColor: alpha('#000', 0.06),
        }}
      >
        <Box sx={{ maxWidth: 720, mx: 'auto', display: 'flex', justifyContent: 'center' }}>
          {clinic.logo ? (
            <Box
              component="img"
              src={clinic.logo}
              alt={`${clinic.name} logo`}
              sx={{ maxHeight: { xs: 56, md: 72 }, maxWidth: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Typography variant="h5" sx={{ color: PRIMARY, fontWeight: 700 }}>
              {clinic.name}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          px: { xs: 2, sm: 3 },
          py: { xs: 3, md: 5 },
        }}
      >
        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 720,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: alpha('#000', 0.06),
            boxShadow: '0 12px 40px rgba(21, 101, 192, 0.08)',
          }}
        >
          <Box
            sx={{
              px: { xs: 3, md: 5 },
              py: { xs: 4, md: 5 },
              textAlign: 'center',
              background: `linear-gradient(180deg, ${alpha(SUCCESS, 0.06)} 0%, transparent 100%)`,
            }}
          >
            <Box
              component={motion.div}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.15 }}
              sx={{ display: 'inline-flex', mb: 2 }}
            >
              <CheckCircleOutline sx={{ fontSize: { xs: 64, md: 72 }, color: SUCCESS }} />
            </Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}
            >
              Confirmation Received
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: SUCCESS,
                fontWeight: 600,
                maxWidth: 480,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              {clinic.confirmationMessage}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 0.5, textAlign: 'center' }}
            >
              Thank you for your interest
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', mb: 3 }}
            >
              Reach out to {clinic.name} using any of the options below.
            </Typography>

            <Grid container spacing={2}>
              {clinic.phone && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ContactItem
                    icon={<Phone fontSize="small" />}
                    label="Phone"
                    value={clinic.phone}
                    href={`tel:${clinic.phone}`}
                  />
                </Grid>
              )}
              {clinic.email && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ContactItem
                    icon={<Email fontSize="small" />}
                    label="Email"
                    value={clinic.email}
                    href={`mailto:${clinic.email}`}
                  />
                </Grid>
              )}
              {clinic.website && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ContactItem
                    icon={<Language fontSize="small" />}
                    label="Website"
                    value={clinic.website}
                    href={websiteUrl}
                  />
                </Grid>
              )}
              {clinic.sms && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ContactItem
                    icon={<Sms fontSize="small" />}
                    label="SMS"
                    value={clinic.sms}
                    href={`sms:${clinic.sms}`}
                  />
                </Grid>
              )}
              {address && (
                <Grid size={12}>
                  <ContactItem
                    icon={<LocationOn fontSize="small" />}
                    label="Address"
                    value={address}
                  />
                </Grid>
              )}
            </Grid>

            <SmsConfirmationHours hours={clinic.hours} />

            {clinic.phone && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="center"
                sx={{ mt: 4 }}
              >
                <Button
                  variant="contained"
                  startIcon={<Phone />}
                  href={`tel:${clinic.phone}`}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    backgroundColor: PRIMARY,
                    '&:hover': { backgroundColor: '#0d47a1' },
                  }}
                >
                  Call Us
                </Button>
                {websiteUrl && (
                  <Button
                    variant="outlined"
                    startIcon={<Language />}
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      borderColor: PRIMARY,
                      color: PRIMARY,
                      '&:hover': { borderColor: '#0d47a1', backgroundColor: alpha(PRIMARY, 0.04) },
                    }}
                  >
                    Visit Website
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        </Paper>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: alpha('#000', 0.06),
          backgroundColor: alpha('#fff', 0.6),
        }}
      >
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} {clinic.name}. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}

export default SmsConfirmation;
